// עורך ערך לכלל מפרט לפי סוג השדה.
import { useState } from 'react';
import type { SpecFieldKey } from '@bb/schema';
import { Field } from '../ui';

const NUM_KEYS = ['zipMaxKb', 'unzippedMaxKb', 'maxDurationMs', 'maxLoops', 'fps'];
const BOOL_KEYS = ['allowExternalJs', 'allowWebFonts', 'allowSvg', 'allowExternalRequests', 'backupImageRequired'];
const LIST_KEYS = ['formats', 'forbiddenFiles'];
export const SPEC_UNITS: Partial<Record<SpecFieldKey, string>> = { zipMaxKb: 'KB', unzippedMaxKb: 'KB', maxDurationMs: 'ms', fps: 'fps' };
const SELECTS: Partial<Record<SpecFieldKey, Record<string, string>>> = {
  weightBasis: { files: 'משקל הקבצים', initialLoad: 'טעינה ראשונית', totalLoad: 'טעינה כוללת' },
  clickMethod: { clickTag: 'clickTag', clickTAG: 'clickTAG', exitApi: 'Exit API', other: 'אחר' },
};

function toText(key: SpecFieldKey, v: unknown): string {
  if (v === undefined || v === null || v === 'unknown') return '';
  if (key === 'sizes' && Array.isArray(v)) return v.map((s: { width: number; height: number }) => `${s.width}x${s.height}`).join(', ');
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/** מחזיר ערך מנותח או שגיאה */
export function parseSpecValue(key: SpecFieldKey, text: string): { value?: unknown; error?: string } {
  const t = text.trim();
  if (!t) return { error: 'ערך ריק — לסימון כלא ידוע השתמשו בכפתור "לא ידוע"' };
  if (key === 'sizes') {
    const parts = t.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);
    const out: { width: number; height: number }[] = [];
    for (const p of parts) {
      const m = /^(\d{1,4})\s*[x×X*]\s*(\d{1,4})$/.exec(p);
      if (!m) return { error: `מידה לא תקינה: "${p}" (פורמט 300x250)` };
      out.push({ width: +m[1], height: +m[2] });
    }
    return { value: out };
  }
  if (NUM_KEYS.includes(key)) { const n = Number(t); return Number.isFinite(n) && n >= 0 ? { value: n } : { error: 'מספר לא תקין' }; }
  if (BOOL_KEYS.includes(key)) return t === 'true' ? { value: true } : t === 'false' ? { value: false } : { error: 'בחרו כן/לא' };
  if (LIST_KEYS.includes(key)) return { value: t.split(',').map((x) => x.trim()).filter(Boolean) };
  return { value: t };
}

export function SpecValueInput({ fieldKey, initial, onSubmit, onCancel, busy }: { fieldKey: SpecFieldKey; initial: unknown; onSubmit: (value: unknown, note: string) => void; onCancel: () => void; busy: boolean }) {
  const [text, setText] = useState(toText(fieldKey, initial));
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const r = parseSpecValue(fieldKey, text);
    if (r.error) { setErr(r.error); return; }
    onSubmit(r.value, note.trim() || 'הוזן ידנית');
  };
  const id = `sv-${fieldKey}`;
  let input;
  if (BOOL_KEYS.includes(fieldKey)) input = <select id={id} value={text} onChange={(e) => setText(e.target.value)}><option value="">בחרו…</option><option value="true">כן</option><option value="false">לא</option></select>;
  else if (SELECTS[fieldKey]) input = <select id={id} value={text} onChange={(e) => setText(e.target.value)}><option value="">בחרו…</option>{Object.entries(SELECTS[fieldKey]!).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>;
  else if (fieldKey === 'notes' || fieldKey === 'packageNaming') input = <textarea id={id} value={text} onChange={(e) => setText(e.target.value)} />;
  else input = <input id={id} className={NUM_KEYS.includes(fieldKey) || fieldKey === 'sizes' || LIST_KEYS.includes(fieldKey) || ['clickTagName', 'entryFile', 'backupImageFormat'].includes(fieldKey) ? 'ltr mono' : ''} inputMode={NUM_KEYS.includes(fieldKey) ? 'numeric' : undefined} value={text} onChange={(e) => setText(e.target.value)} />;
  const hint = fieldKey === 'sizes' ? 'לדוגמה: 300x250, 728x90' : LIST_KEYS.includes(fieldKey) ? 'מופרד בפסיקים' : SPEC_UNITS[fieldKey] ? `יחידה: ${SPEC_UNITS[fieldKey]}` : undefined;
  return (
    <form className="stack" onSubmit={submit} style={{ marginTop: 6 }}>
      <Field label="ערך ידני" hint={hint}>{input}</Field>
      <Field label="מקור / הערה (נשמר כציטוט)"><input value={note} maxLength={400} onChange={(e) => setNote(e.target.value)} placeholder="לדוגמה: אושר טלפונית מול נציג המדיה" /></Field>
      {err && <div className="small" role="alert" style={{ color: 'var(--danger)' }}>{err}</div>}
      <div className="row"><button className="btn small primary" disabled={busy}>{busy ? 'שומר…' : 'שמירה'}</button><button type="button" className="btn small" onClick={onCancel}>ביטול</button></div>
    </form>
  );
}
